// Shared Navigation Component for all pages - Simplified Client Navigation
import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LotusLogo } from "./SharedComponents";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Simplified navigation items as per client request
  const navItems = [
    { name: "About Me", href: "/about" },
    { name: "Yoga Classes", href: "/yoga-classes" },
    { name: "Treatments", href: "/treatments" },
    { name: "Women's Circles", href: "/womens-circles" },
    { name: "Retreats", href: "/retreats" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/98 nav-blur shadow-sm" : "bg-cream/95 nav-blur"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <LotusLogo className="w-10 h-10" />
            <span className="font-heading text-xl tracking-wide text-neutral-800">
              Annelie Lily
            </span>
          </Link>

          {/* Desktop Navigation - Clean & Simple */}
          <div className="hidden lg:flex items-center gap-6">
            {navItems.map((item) => (
              <Link 
                key={item.href}
                to={item.href} 
                className={`text-sm tracking-wide transition-colors duration-300 font-medium ${
                  location.pathname === item.href 
                    ? "text-gold" 
                    : "text-neutral-700 hover:text-gold"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 text-neutral-800"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu - Clean & Simple */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-cream/98 nav-blur border-t border-gold/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 space-y-4">
              {navItems.map((item) => (
                <Link 
                  key={item.href}
                  to={item.href} 
                  className={`block text-lg font-heading ${
                    location.pathname === item.href 
                      ? "text-gold" 
                      : "text-neutral-800 hover:text-gold"
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
